import React, { useState, useEffect } from 'react';
import { Shield, Inbox, Check, X, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLeads = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('pending');

  const headers = () => ({ 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/leads`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      });
      if (res.ok) { setAuthed(true); load(); }
      else setMessage('Incorrect admin key');
    } catch (err) {
      console.error('Admin auth failed:', err);
      setMessage('Unable to reach server. Try again.');
    }
  };

  const approve = async (lead) => {
    if (!window.confirm(`Approve ${lead.company_name} and provision their dashboard?\n\nThis sends them a welcome email at ${lead.email}.`)) return;
    setMessage(`Approving ${lead.company_name}...`);
    const res = await fetch(`${BACKEND_URL}/api/admin/leads/${lead.id}/approve`, {
      method: 'POST', headers: headers(),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setMessage(`✓ ${lead.company_name} approved → advertiser ${data.advertiser_id?.slice(0, 8)}...`);
      await load();
    } else {
      setMessage(data.detail || 'Approve failed');
    }
  };

  const decline = async (lead) => {
    const reason = window.prompt(`Decline ${lead.company_name}?\n\nWhat's the reason? (will be sent in their email)`,
      "we're not currently serving your area");
    if (reason === null) return;
    setMessage(`Declining ${lead.company_name}...`);
    const res = await fetch(`${BACKEND_URL}/api/admin/leads/${lead.id}/decline`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ reason }),
    });
    if (res.ok) {
      setMessage(`✗ ${lead.company_name} declined`);
      await load();
    } else {
      setMessage('Decline failed');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm">
          <div className="flex flex-col items-center mb-4">
            <Shield className="text-navy mb-2" size={32} />
            <h1 className="text-xl font-bold text-navy">Admin Access</h1>
          </div>
          <input
            type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key" autoFocus
            className="w-full px-3 py-2.5 border border-navy/20 rounded-lg mb-3 focus:border-navy focus:outline-none"
            data-testid="admin-key-input"
          />
          {message && <p className="text-red-600 text-sm mb-3">{message}</p>}
          <button type="submit" className="w-full bg-navy text-cream py-2.5 rounded-lg font-semibold hover:bg-navy/90" data-testid="login-btn">
            Enter
          </button>
        </form>
      </div>
    );
  }

  const counts = {
    pending: leads.filter(l => l.status === 'pending').length,
    approved: leads.filter(l => l.status === 'approved').length,
    declined: leads.filter(l => l.status === 'declined').length,
    all: leads.length,
  };
  const visible = filter === 'all' ? leads : leads.filter(l => l.status === filter);

  return (
    <div className="min-h-screen bg-cream">
      <section className="bg-navy py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-cream flex items-center gap-2"><Inbox size={22} /> Lead Triage</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <a href="/admin/geofences" style={{ color: 'var(--cream)' }} className="opacity-80 hover:opacity-100 transition-opacity underline" data-testid="leads-nav-geofences">Geofences</a>
            <a href="/admin/advertisers" style={{ color: 'var(--cream)' }} className="opacity-80 hover:opacity-100 transition-opacity underline" data-testid="leads-nav-advertisers">Advertisers</a>
            <a href="/admin/manual-data" style={{ color: 'var(--cream)' }} className="opacity-80 hover:opacity-100 transition-opacity underline" data-testid="leads-nav-manual-data">Manual data</a>
            <a href="/admin/idle-heatmap" style={{ color: 'var(--cream)' }} className="opacity-80 hover:opacity-100 transition-opacity underline" data-testid="leads-nav-idle-heatmap">Idle heatmap</a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {['pending', 'approved', 'declined', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg ${filter === f ? 'bg-navy text-cream' : 'bg-white text-navy/60 border border-navy/10'}`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
            </button>
          ))}
        </div>

        {message && <div className="bg-navy/5 text-navy text-sm px-4 py-2 rounded-lg mb-3" data-testid="status-message">{message}</div>}

        {loading ? (
          <p className="text-center text-navy/50 p-8 bg-white rounded-2xl">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-navy/50 p-8 bg-white rounded-2xl">No {filter} leads.</p>
        ) : (
          <div className="space-y-3">
            {visible.map(lead => (
              <div key={lead.id} className="bg-white rounded-2xl shadow-sm p-5" data-testid={`lead-card-${lead.id}`}>
                <div className="flex flex-col md:flex-row items-start justify-between gap-3">
                  <div className="w-full md:flex-1 min-w-0 break-words" data-testid={`lead-details-${lead.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-navy max-w-full" data-testid={`lead-company-${lead.id}`}>{lead.company_name}</h3>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        lead.status === 'pending'  ? 'bg-amber-100 text-amber-700' :
                        lead.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>{lead.status}</span>
                      <span className="text-xs text-navy/40 flex items-center gap-1"><Clock size={11} /> {lead.timestamp ? new Date(lead.timestamp).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-sm text-navy/70 mt-1 break-all" data-testid={`lead-contact-${lead.id}`}>{lead.email} · {lead.phone}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs text-navy/70">
                      <div data-testid={`lead-wrap-type-${lead.id}`}><span className="text-navy/40">Wrap:</span> {lead.wrap_type || '—'}</div>
                      <div data-testid={`lead-duration-${lead.id}`}><span className="text-navy/40">Duration:</span> {lead.campaign_duration || '—'} mo</div>
                      <div data-testid={`lead-driver-count-${lead.id}`}><span className="text-navy/40">Drivers:</span> {lead.driver_count || 1}</div>
                      <div data-testid={`lead-zone-count-${lead.id}`}><span className="text-navy/40">Mini-zones:</span> {(lead.mini_zones || []).length}</div>
                    </div>
                    {lead.decline_reason && (
                      <p className="text-xs text-red-700 mt-2">Decline reason: {lead.decline_reason}</p>
                    )}
                    {lead.provisioned_advertiser_id && (
                      <p className="text-xs text-green-700 mt-2">→ Advertiser ID: {lead.provisioned_advertiser_id}</p>
                    )}
                  </div>
                  {lead.status === 'pending' && (
                    <div className="flex flex-wrap items-center gap-2 max-w-full" data-testid={`lead-actions-${lead.id}`}>
                      <button onClick={() => approve(lead)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md"
                        data-testid={`approve-${lead.id}`}>
                        <Check size={12} /> Approve & Provision
                      </button>
                      <button onClick={() => decline(lead)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md"
                        data-testid={`decline-${lead.id}`}>
                        <X size={12} /> Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
